<template>
  <div class="bio-tab">
    <h3 class="gm-section-title">Character Biography</h3>

    <!-- Character Avatar Section -->
    <div class="avatar-section gm-panel">
      <h4 class="gm-section-title">Character Avatar</h4>
      <div class="avatar-container">
        <div class="avatar-preview">
          <img
            v-if="currentAvatar"
            :src="currentAvatar"
            alt="Character Avatar"
            class="avatar-image"
            @error="onImageError"
          />
          <div v-else class="avatar-placeholder">
            <span class="placeholder-icon">📷</span>
            <span class="placeholder-text">No Avatar</span>
          </div>
        </div>

        <div class="avatar-controls">
          <div class="upload-section">
            <label class="upload-button gm-btn" for="avatar-upload">
              <span class="upload-icon">📁</span>
              Upload Image
            </label>
            <input
              id="avatar-upload"
              type="file"
              accept="image/*"
              @change="handleFileUpload"
              class="file-input"
            />
          </div>

          <div class="url-section gm-field">
            <label class="gm-label">Or enter image URL:</label>
            <div class="url-input-group">
              <input
                type="url"
                :value="avatarUrl"
                @input="updateAvatarUrl"
                placeholder="https://example.com/image.jpg"
                class="url-input gm-input"
              />
              <button
                @click="setAvatarFromUrl"
                :disabled="!avatarUrl"
                class="set-url-button gm-btn gm-btn--primary gm-btn--sm"
                title="Set avatar from URL"
              >
                Set
              </button>
            </div>
          </div>

          <button
            v-if="currentAvatar"
            @click="removeAvatar"
            class="remove-button gm-btn gm-btn--danger gm-btn--sm"
            title="Remove current avatar"
          >
            🗑️ Remove Avatar
          </button>
        </div>
      </div>
    </div>

    <!-- Character Details Section -->
    <div class="details-section gm-panel">
      <div class="bio-section">
        <div class="bio-field gm-field">
          <label class="gm-label">Age</label>
          <input
            type="number"
            class="gm-input"
            :value="template.data.bio.age"
            @input="updateAge"
            min="1"
            placeholder="25"
          />
        </div>
        <div class="bio-field gm-field">
          <label class="gm-label">Gender</label>
          <input
            type="text"
            class="gm-input"
            :value="template.data.bio.gender"
            @input="updateGender"
            placeholder="Male/Female/Other"
          />
        </div>
      </div>

      <div class="bio-field full-width gm-field">
        <label class="gm-label">Appearance</label>
        <textarea
          class="gm-textarea"
          :value="template.data.bio.appearance"
          @input="updateAppearance"
          placeholder="Describe the character's physical appearance, clothing, notable features..."
          rows="4"
        ></textarea>
      </div>

      <div class="bio-field full-width gm-field">
        <label class="gm-label">Background</label>
        <textarea
          class="gm-textarea"
          :value="template.data.bio.background"
          @input="updateBackground"
          placeholder="Character's history, personality, motivations, goals..."
          rows="6"
        ></textarea>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BioTab',
  props: {
    template: {
      type: Object,
      required: true
    },
    service: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      avatarUrl: '',
      uploadingImage: false,
      imageError: false
    };
  },
  computed: {
    currentAvatar() {
      return this.template.data.bio.avatar || null;
    }
  },
  methods: {
    updateAge(event) {
      this.service.setBio({
        ...this.template.data.bio,
        age: parseInt(event.target.value, 10)
      });
      this.$emit('update');
    },
    updateGender(event) {
      this.service.setBio({
        ...this.template.data.bio,
        gender: event.target.value
      });
      this.$emit('update');
    },
    updateAppearance(event) {
      this.service.setBio({
        ...this.template.data.bio,
        appearance: event.target.value
      });
      this.$emit('update');
    },
    updateBackground(event) {
      this.service.setBio({
        ...this.template.data.bio,
        background: event.target.value
      });
      this.$emit('update');
    },
    
    // Avatar management methods
    updateAvatarUrl(event) {
      this.avatarUrl = event.target.value;
    },
    
    setAvatarFromUrl() {
      if (this.avatarUrl.trim()) {
        this.updateAvatar(this.avatarUrl.trim());
        this.avatarUrl = '';
      }
    },
    
    updateAvatar(avatarUrl) {
      this.service.setBio({
        ...this.template.data.bio,
        avatar: avatarUrl
      });
      this.$emit('update');
      this.imageError = false;
    },
    
    removeAvatar() {
      if (confirm('Are you sure you want to remove the current avatar?')) {
        this.service.setBio({
          ...this.template.data.bio,
          avatar: null
        });
        this.$emit('update');
        this.avatarUrl = '';
        this.imageError = false;
      }
    },
    
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file.');
        return;
      }
      
      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        alert('Image file is too large. Please select an image smaller than 5MB.');
        return;
      }
      
      this.uploadingImage = true;
      
      // Create a FileReader to convert the file to a data URL
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const dataUrl = e.target.result;
          this.updateAvatar(dataUrl);
          console.log('Image uploaded successfully as data URL');
        } catch (error) {
          console.error('Error processing image:', error);
          alert('Error processing the image. Please try again.');
        } finally {
          this.uploadingImage = false;
          // Clear the file input
          event.target.value = '';
        }
      };
      
      reader.onerror = () => {
        console.error('Error reading file');
        alert('Error reading the file. Please try again.');
        this.uploadingImage = false;
        event.target.value = '';
      };
      
      reader.readAsDataURL(file);
    },
    
    onImageError() {
      console.warn('Failed to load avatar image:', this.currentAvatar);
      this.imageError = true;
    }
  }
};
</script>

<style scoped>
.bio-tab {
  padding: 20px 0;
}

/* Avatar Section */
.avatar-section {
  margin-bottom: var(--gm-gap-lg);
}

.avatar-container {
  display: flex;
  gap: var(--gm-gap-lg);
  align-items: flex-start;
}

.avatar-preview {
  flex-shrink: 0;
  width: 150px;
  height: 150px;
  border-radius: var(--gm-radius);
  overflow: hidden;
  border: 1px solid var(--gm-border-dim);
  background: var(--gm-surface-sunken);
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--gm-text-dim);
  text-align: center;
}

.placeholder-icon {
  font-size: 2rem;
  margin-bottom: var(--gm-gap-sm);
  opacity: 0.7;
}

.placeholder-text {
  font-size: 0.8rem;
  color: var(--gm-text-muted);
}

.avatar-controls {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap-lg);
}

/* Upload Section */
.upload-section {
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap-sm);
}

.upload-button {
  width: fit-content;
}

.file-input {
  display: none;
}

/* URL Section */
.url-input-group {
  display: flex;
  gap: var(--gm-gap-sm);
  align-items: center;
}

.url-input {
  flex: 1;
}

/* Details Section */
.bio-section {
  display: flex;
  gap: var(--gm-gap);
  margin-bottom: var(--gm-gap);
}

.bio-field {
  flex: 1;
  margin-bottom: var(--gm-gap);
}

.bio-field.full-width {
  width: 100%;
}

/* Headers */
h3 {
  text-align: center;
  padding-bottom: var(--gm-gap-sm);
  border-bottom: 1px solid var(--gm-border-dim);
}

h4 {
  margin-top: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .avatar-container {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
  
  .avatar-preview {
    width: 120px;
    height: 120px;
  }
  
  .avatar-controls {
    width: 100%;
  }
  
  .bio-section {
    flex-direction: column;
    gap: 0;
  }
  
  .url-input-group {
    flex-direction: column;
    align-items: stretch;
  }
  
  .set-url-button {
    margin-top: 8px;
  }
}

@media (max-width: 480px) {
  .avatar-section,
  .details-section {
    padding: 15px;
  }
  
  .avatar-preview {
    width: 100px;
    height: 100px;
  }
  
  .upload-button,
  .remove-button {
    padding: 10px 16px;
    font-size: 14px;
  }
}
</style>